import { yupResolver } from '@hookform/resolvers/yup'
import React, { ChangeEvent, FunctionComponent, useEffect } from 'react'
import { Col, Container, Row } from 'react-grid-system'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { object, string } from 'yup'

import { ClinTheme } from '../../../ClinTheme'
import { ClinButton } from '../../../components/ClinButton'
import { ClinGroup } from '../../../components/ClinGroup'
import { ClinPageContentFrame } from '../../../components/ClinPageContentFrame'
import { ClinSelect } from '../../../components/ClinSelect'
import { ClinSpacer } from '../../../components/ClinSpacer'
import { ClinSpinner } from '../../../components/ClinSpinner'
import { StyledSpinnerContainer } from '../../../components/ClinSpinner/ClinSpinner.styles'
import { ClinText } from '../../../components/ClinText'
import { TypographyVariant } from '../../../components/ClinText/ClinText.styles'
import { ClinTextInput } from '../../../components/ClinTextInput'
import { UserRole, isAusGaUser } from '../../../constants'
import { Prompt } from '../../../features/Prompt'
import { useCheckUnableHomePage } from '../../../hooks/useCheckUnableHomePage/useCheckUnableHomePage'
import { CountryDto, NewInstituteRequestDto } from '../../../types/swaggerTypes'
import {
  getInputPlaceholder,
  PlaceholderType
} from '../../../utils/Forms/getInputPlaceholder'
import {
  getInputValidation,
  ValidationType
} from '../../../utils/Forms/getInputValidation'
import { requiredMinStringLength } from '../../../utils/Forms/requiredMinStringLength'
import {
  StyledLoadingContainer,
  StyledLoadingSpinner
} from '../InviteNewUser/InviteNewUser.styles'

const defaultState: NewInstituteRequestDto = {
  referrerName: '',
  referrerPhone: '',
  instituteName: '',
  address: {
    address1: '',
    address2: '',
    address3: '',
    county: '',
    city: '',
    postalCode: '',
    country: ''
  },
  anyOtherInformation: ''
}

interface IRequestAccessProps {
  /** Whether we are loading or not */
  isLoading: boolean
  /** Informs the view when the form is submitting */
  isSubmitting: boolean
  /** Decides whether the page is edited or not */
  isPageEdited: boolean
  /** An array of provided countries */
  countries: CountryDto[]
  /** User country */
  userCountry: string
  /** User country */
  userRole?: UserRole
  /** Handle form submission */
  handleFormSubmission: (data: NewInstituteRequestDto) => void
  /** Handle whether the page is edited or not */
  handleIsPageEdited: (isEdited: boolean) => void
  /** Handle the user requesting to leave the page */
  handleCancel: () => void
}

export const RequestAccess: FunctionComponent<IRequestAccessProps> = ({
  isLoading,
  isSubmitting,
  isPageEdited,
  countries,
  userCountry,
  userRole,
  handleFormSubmission,
  handleIsPageEdited,
  handleCancel
}) => {
  const { t } = useTranslation()
  const max240 = getInputValidation(ValidationType.MaxiumumCharacters, 240)
  const max100 = getInputValidation(ValidationType.MaxiumumCharacters, 100)
  const max80 = getInputValidation(ValidationType.MaxiumumCharacters, 80)
  const max60 = getInputValidation(ValidationType.MaxiumumCharacters, 60)

  const { enableNewHomePage } = useCheckUnableHomePage()

  const requestAccessSchema = object().shape({
    referrerName: string().max(80, max80),
    referrerPhone: string()
      .max(80, max80)
      .matches(
        /^(?!.*[a-zA-Z])/,
        getInputValidation(ValidationType.OnlyNumbersAndSpecialChars)
      ),
    instituteName: requiredMinStringLength(80),
    address: object().shape({
      address1: requiredMinStringLength(240),
      address2: string().max(240, max240),
      address3: string().max(240, max240),
      county: string().max(60, max60),
      city: string().max(100, max100),
      postalCode: string().max(100, max100),
      country: requiredMinStringLength(80)
    }),
    anyOtherInformation: string()
  })

  const {
    control,
    formState,
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<NewInstituteRequestDto>({
    defaultValues: defaultState,
    resolver: yupResolver(requestAccessSchema)
  })

  useEffect(() => {
    handleIsPageEdited(Object.keys(formState.dirtyFields).length > 0)
  }, [formState, handleIsPageEdited])

  return isLoading ? (
    <Container>
      <Row align="center">
        <Col>
          <StyledSpinnerContainer>
            <ClinSpinner size={ClinTheme.space[7]} />
          </StyledSpinnerContainer>
        </Col>
      </Row>
    </Container>
  ) : (
    <ClinPageContentFrame
      crumbs={[
        isAusGaUser(userCountry, userRole) && enableNewHomePage
          ? {
              path: '/home',
              name: t('navigation:home')
            }
          : { path: '/orders', name: t('orders:page_name') },
        {
          name: t('request_access:page_name'),
          path: '/institutes/request-access'
        }
      ]}
    >
      {isPageEdited && <Prompt />}
      <Row>
        <Col sm={12}>
          <ClinText
            as="h1"
            variant={TypographyVariant.H2}
            fontWeight={ClinTheme.fontWeights.bold}
          >
            {t('request_access:title')}
          </ClinText>
        </Col>
      </Row>
      <Row>
        <Col sm={12} md={10} lg={5}>
          <ClinText as="p" variant={TypographyVariant.LargeParagraph}>
            {t('request_access:description')}
          </ClinText>
          <ClinSpacer />
          <ClinText variant={TypographyVariant.LargeParagraph}>
            {t('request_access:mandatory_description')}
          </ClinText>
        </Col>
      </Row>
      <Row>
        <Col sm={12} md={8} lg={4}>
          <form onSubmit={handleSubmit(handleFormSubmission)}>
            <ClinSpacer height={ClinTheme.space[4]} />
            <ClinTextInput
              id="referrer-name"
              {...register('referrerName')}
              label={t('request_access:referrer_name')}
              width="100%"
              hasError={!!(errors && errors.referrerName)}
              prompt={errors.referrerName?.message}
            />
            <ClinSpacer height={ClinTheme.space[4]} />

            <ClinTextInput
              id="referrer-phone"
              {...register('referrerPhone')}
              label={t('request_access:referrer_phone')}
              width="100%"
              hasError={!!(errors && errors.referrerPhone)}
              prompt={errors.referrerPhone?.message}
            />
            <ClinSpacer height={ClinTheme.space[4]} />

            <ClinTextInput
              id="institute-name"
              {...register('instituteName')}
              label={t('request_access:institute_name')}
              width="100%"
              hasError={!!(errors && errors.instituteName)}
              prompt={errors.instituteName?.message}
            />
            <ClinSpacer height={ClinTheme.space[4]} />

            <ClinTextInput
              id="address-1"
              {...register('address.address1')}
              label={t('request_access:address_line_one')}
              width="100%"
              hasError={!!(errors && errors.address && errors.address.address1)}
              prompt={errors?.address?.address1?.message}
            />
            <ClinSpacer height={ClinTheme.space[4]} />

            <ClinTextInput
              id="address-2"
              {...register('address.address2')}
              label={t('request_access:address_line_two')}
              width="100%"
              hasError={!!(errors && errors.address && errors.address.address2)}
              prompt={errors?.address?.address2?.message}
            />

            <ClinSpacer height={ClinTheme.space[4]} />

            <ClinTextInput
              id="address-3"
              {...register('address.address3')}
              label={t('request_access:address_line_three')}
              width="100%"
              hasError={!!(errors && errors.address && errors.address.address3)}
              prompt={errors?.address?.address3?.message}
            />

            <ClinSpacer height={ClinTheme.space[4]} />

            <Row>
              <Col sm={12} md={6}>
                <ClinTextInput
                  id="address-city"
                  {...register('address.city')}
                  label={t('request_access:city')}
                  width="100%"
                  hasError={!!(errors && errors.address && errors.address.city)}
                  prompt={errors?.address?.city?.message}
                />
              </Col>

              <Col sm={12} md={6}>
                <ClinTextInput
                  id="address-county"
                  {...register('address.county')}
                  label={t('request_access:county')}
                  width="100%"
                  hasError={
                    !!(errors && errors.address && errors.address.county)
                  }
                  prompt={errors?.address?.county?.message}
                />
              </Col>
            </Row>

            <ClinSpacer height={ClinTheme.space[4]} />

            <Controller
              name={'address.country'}
              control={control}
              render={({ field: { onChange, value } }) => (
                <ClinSelect
                  id="country"
                  label={t('request_access:country')}
                  width="100%"
                  value={value}
                  onChange={(changeValue: ChangeEvent<HTMLSelectElement>) =>
                    onChange(changeValue.currentTarget.value)
                  }
                  hasError={!!(errors && errors.address?.country)}
                  prompt={errors.address?.country?.message}
                >
                  <option value="" disabled={true}>
                    {getInputPlaceholder(PlaceholderType.SelectInput)}
                  </option>
                  {countries
                    .sort((a, b) =>
                      a.countryName && b.countryName
                        ? a.countryName.localeCompare(b.countryName)
                        : -1
                    )
                    .map((country: CountryDto, index: number) => {
                      return (
                        <option
                          key={`country-codes-${index}`}
                          value={country.countryCode}
                        >
                          {country.countryName}
                        </option>
                      )
                    })}
                </ClinSelect>
              )}
            />
            <ClinSpacer height={ClinTheme.space[4]} />

            <Row>
              <Col sm={12} md={6}>
                <ClinTextInput
                  id="postal-code"
                  {...register('address.postalCode')}
                  label={t('request_access:postcode')}
                  width="100%"
                  hasError={
                    !!(errors && errors.address && errors.address.postalCode)
                  }
                  prompt={errors?.address?.postalCode?.message}
                />
              </Col>
            </Row>

            <div style={{ display: 'none' }}>
              <ClinTextInput
                id="any-other-information"
                {...register('anyOtherInformation')}
                value=" "
              />
            </div>

            <ClinSpacer height={ClinTheme.space[8]} hasBorder />

            <ClinGroup>
              <ClinButton onClick={() => handleCancel()}>
                {t('common:buttons.cancel')}
              </ClinButton>
              <StyledLoadingContainer>
                <ClinButton
                  type="submit"
                  variant="primary"
                  disabled={isSubmitting || !isPageEdited}
                >
                  {t('request_access:submit_request_btn')}
                </ClinButton>
                {isSubmitting && (
                  <StyledLoadingSpinner>
                    <ClinSpinner />
                  </StyledLoadingSpinner>
                )}
              </StyledLoadingContainer>
            </ClinGroup>

            <ClinSpacer height={ClinTheme.space[8]} />
          </form>
        </Col>
      </Row>
    </ClinPageContentFrame>
  )
}
